import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';
import { transformToHierarchy } from '../util/tree';
import SelectUsersEdit from '../docsComponents/SelectUsersEdit';
import '../docsComponents/addDocsModal.css';

interface AddModalProps {
  category: any,
  selectedCategoryId: any,
  handleClickCategoryBox: any,
  categoryName: any,
  isCategoryListVisible: any,
  handleCategoryListClick: any,
  handleNodeSelect: any,
  handleClickAddCategoryId: any,
  refreshCategoryDocs: any,
  documentId: any,
  setSelectedCategoryId: any,
  setCategoryName: any,
  none: any,
  handleCloseEdit: any,
  baseUrl?: any
}

interface UserOption { value: number; label: string; }
type Mode = null | 'create' | 'upload';

const EditDocs: React.FC<AddModalProps> = ({
  category,
  selectedCategoryId,
  handleClickCategoryBox,
  categoryName,
  isCategoryListVisible,
  handleCategoryListClick,
  handleNodeSelect,
  handleClickAddCategoryId,
  refreshCategoryDocs,
  documentId,
  setSelectedCategoryId,
  setCategoryName,
  none,
  handleCloseEdit,
  baseUrl
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dayLearn, setDeyLearn] = useState('10');
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
  const [active, setActive] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [file, setFile] = useState<File | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [categoryId, setCategoryId] = useState();
  const [mode, setMode] = useState<Mode>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string>('');
  const [initialContent, setInitialContent] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentApplied = useRef(false);
  const previewUrl = file ? URL.createObjectURL(file) : '';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`document/${documentId}`);
        const { data: Users } = await axios.get(`users`);
        const formattedUsers = Users.map((user: any) => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`,
        }));
        setUsers(formattedUsers);

        if (data) {
          setTitle(data.title);
          setDescription(data.description);
          setDeyLearn(data.day_learn);
          setActive(data.isForAll);
          const userIds = data.user?.map((u: any) => u.id) || [];
          setSelectedTasks(userIds);
          setSelectedOptions(formattedUsers.filter((user: any) => userIds.includes(user.value)));
          setCategoryId(data.documentCategoryId);

          // detect the document kind
          if (data.content) {
            setInitialContent(data.content);
            setMode('create');
          } else if (data.file_url) {
            setExistingFileUrl(data.file_url);
            setMode('upload');
          } else {
            setMode(null);
          }
        }
      } catch (e) {
        console.error('Error loading data', e);
      }
    })();
  }, [documentId]);

  // load category name once categoryId is known
  useEffect(() => {
    (async () => {
      if (!categoryId) return;
      try {
        const { data } = await axios.get(`document-category/${categoryId}`);
        if (data) {
          setSelectedCategoryId(data.id);
          setCategoryName(data.name);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [categoryId, setSelectedCategoryId, setCategoryName]);

  // hydrate the editor once it is mounted
  useEffect(() => {
    if (mode === 'create' && sheetRef.current && !contentApplied.current) {
      sheetRef.current.innerHTML = initialContent || '';
      contentApplied.current = true;
    }
  }, [mode, initialContent]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    sheetRef.current?.focus();
  };

  const handleFile = (f: File | null) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) {
      enqueueSnackbar(t('OnlyPdf') as string, { variant: 'error' });
      return;
    }
    if (f.size > 1 * 1024 * 1024) {
      enqueueSnackbar('File size must be less than 1MB', { variant: 'error' });
      return;
    }
    setFile(f);
    setMode('upload');
  };

  const resetLeft = () => {
    setMode(null);
    setFile(null);
    setExistingFileUrl('');
    contentApplied.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!title.trim()) {
      enqueueSnackbar('Enter name please', { variant: 'error' });
      return;
    }
    if (!active && (!selectedTasks || selectedTasks.length === 0)) {
      enqueueSnackbar('Please select users', { variant: 'error' });
      return;
    }
    if (file && file.size > 1 * 1024 * 1024) {
      enqueueSnackbar('File size must be less than 1MB', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const metadata = {
        title,
        description,
        isForAll: active,
        day_learn: Number(dayLearn),
      } as any;

      metadata.documentCategoryId = Array.isArray(selectedCategoryId)
        ? selectedCategoryId[0]
        : selectedCategoryId;

      if (!active) metadata.userId = selectedTasks;
      if (mode === 'create') metadata.content = (sheetRef.current?.innerHTML || '').trim();

      await axios.put(`document/${documentId}`, metadata);

      if (mode === 'upload' && file) {
        const formData = new FormData();
        formData.append('document', file);
        await axios.post(`document/upload/${documentId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      enqueueSnackbar('Uploading success', { variant: 'success' });
      setFile(null);
      refreshCategoryDocs();
      handleCloseEdit();
    } catch (error) {
      console.error('Error updating document:', error);
      enqueueSnackbar('Error uploading file', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const badge = mode === 'create' ? '📝' : mode === 'upload' ? 'PDF' : '＋';
  const leftTitle = mode === 'create' ? t('Document')
    : mode === 'upload' ? (file?.name || title || t('Document'))
      : t('NoDocumentSelected');
  const leftSub = mode === 'create' ? t('EditorDraft')
    : mode === 'upload' && file ? `${(file.size / 1024).toFixed(0)} KB`
      : mode === 'upload' ? t('CurrentFile')
        : t('CreateOrUploadHint');

  const filePreviewSrc = file ? previewUrl : (existingFileUrl ? `${baseUrl || ''}${existingFileUrl}` : '');

  return (
    <div className="adm-modal" role="presentation">
      <div className="adm-accent"><span style={{ width: mode ? '66%' : '33%' }} /></div>

      <div className="adm-head">
        <button className="adm-hbtn" title={t('Cancel') as string} onClick={handleCloseEdit}>✕</button>
        <div className="adm-ttl">
          <h1>{t('EditDocument')}</h1>
          <div className="adm-sub">{t('CreateOrUploadHint')}</div>
        </div>
      </div>

      <div className="adm-body">
        {/* LEFT */}
        <div className="adm-doc">
          <div className="adm-doc-bar">
            <div className="adm-badge" style={mode === 'create' ? { background: 'linear-gradient(135deg,#7c3aed,#a78bfa)' } : {}}>
              {badge}
            </div>
            <div>
              <div className="adm-t">{leftTitle}</div>
              <div className="adm-s">{leftSub}</div>
            </div>
            {mode && (
              <div className="adm-right">
                {mode === 'upload' && <button className="adm-mini-btn" onClick={() => fileInputRef.current?.click()}>↻ {t('ReplaceFile')}</button>}
                <button className="adm-mini-btn" onClick={resetLeft}>← {t('Change')}</button>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files ? e.target.files[0] : null)}
          />

          <div className="adm-stage">
            {!mode && (
              <div className="adm-choice">
                <div className="adm-hint">{t('HowAddDoc')}</div>
                <div className="adm-choice-cards">
                  <div className="adm-ccard create" onClick={() => { setMode('create'); contentApplied.current = false; setTimeout(() => sheetRef.current?.focus(), 50); }}>
                    <div className="adm-ci">📝</div>
                    <div className="adm-ct">{t('CreateDoc')}</div>
                    <div className="adm-cs">{t('OpenEditorHint')}</div>
                  </div>
                  <div className="adm-ccard upload" onClick={() => fileInputRef.current?.click()}>
                    <div className="adm-ci">📤</div>
                    <div className="adm-ct">{t('UploadFile')}</div>
                    <div className="adm-cs">{t('UploadFileHint')}</div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'create' && (
              <div className="adm-editor">
                <div className="adm-tools">
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'h1')} title="H1">H1</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'h2')} title="H2">H2</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', 'p')} title={t('Text') as string}>¶</button>
                  <span className="adm-div" />
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} style={{ fontWeight: 900 }}>B</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} style={{ fontStyle: 'italic' }}>I</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} style={{ textDecoration: 'underline' }}>U</button>
                  <span className="adm-div" />
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}>•</button>
                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}>1.</button>
                </div>
                <div className="adm-editor-scroll">
                  <div
                    ref={sheetRef}
                    className="adm-sheet"
                    contentEditable
                    suppressContentEditableWarning
                    data-ph={t('StartWriting') as string}
                  />
                </div>
              </div>
            )}

            {mode === 'upload' && filePreviewSrc && (
              <div className="adm-preview">
                <iframe title={leftTitle as string} src={filePreviewSrc} />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div className="adm-aside">
          <div className="adm-aside-scroll">
            <div className="adm-panel">
              <h4>{t('Information')}</h4>
              <div className="adm-field">
                <label>{t('Title')} <span className="adm-req">*</span></label>
                <input className="adm-inp" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="adm-field">
                <label>{t('Description')}</label>
                <input className="adm-inp" type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="adm-two">
                <div className="adm-field">
                  <label>{t('DayLearn')}</label>
                  <input
                    className="adm-inp"
                    type="text"
                    value={dayLearn}
                    onChange={(e) => { if (/^\d*$/.test(e.target.value)) setDeyLearn(e.target.value); }}
                  />
                </div>
                <div className="adm-field">
                  <label>{t('Category')}</label>
                  <CategoryTaskPicker
                    nodes={transformToHierarchy(category)}
                    selectedCategoryId={(selectedCategoryId?.[0] ?? -1) > 0 ? selectedCategoryId : []}
                    categoryName={categoryName}
                    onCategorySelect={(id) => { handleNodeSelect(id); handleClickAddCategoryId(Number(id)); }}
                    selectedTasks={[]}
                    setSelectedTasks={() => {}}
                    taskOrTasks={false}
                    categoryOnly
                    block
                    allowGroupSelect
                  />
                </div>
              </div>
            </div>

            <div className="adm-panel">
              <h4>{t('ChooseUsers')}</h4>
              <div className="adm-aud-row">
                <FormControlLabel
                  control={<Switch checked={active} onChange={() => setActive(!active)} />}
                  label={t('AllUsers')}
                />
              </div>
              {!active && (
                <SelectUsersEdit
                  users={users}
                  setSelectedTasks={setSelectedTasks}
                  setSelectedOptions={setSelectedOptions}
                  selectedOptions={selectedOptions}
                />
              )}
            </div>
          </div>

          <div className="adm-foot">
            <Button
              variant="contained"
              sx={{ padding: '8px 10px', fontSize: 14, width: '100%', color: 'white !important' }}
              disabled={submitting}
              onClick={handleSubmit}>
              {t('Change')}
            </Button>
            <Button
              variant="outlined"
              sx={{ padding: '8px 10px', fontSize: 14, width: '100%' }}
              onClick={handleCloseEdit}>
              {t('Cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDocs;
